/**
 * VB-024B.4 — React hooks for tier and capability access.
 *
 * Use these hooks in components to gate features without touching auth state directly.
 * Reads from useAuthStore; no circular dependency risk (no import from useStore).
 *
 * Phase 1.1 — Now reads `profiles.plan` from DB instead of hardcoding FREE.
 * Phase 1.2 — Reads `profiles.trial_ends_at` and applies getEffectiveTier.
 */

import { useState, useEffect } from 'react';
import { useAuthStore } from '../store/useAuthStore';
import {
  hasCapability,
  getEffectiveTier,
  type UserTier,
  type Capability,
} from '../lib/capabilities';
import { supabase } from '../lib/supabase';
import i18n from '../i18n';

/** Returns the current user's tier read from `profiles.plan` in DB, trial-aware. */
export function useUserTier(): UserTier {
  const session = useAuthStore((state) => state.session);
  const [plan, setPlan] = useState<string>('free');
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.user?.id) {
      setPlan('free');
      setTrialEndsAt(null);
      return;
    }

    supabase
      .from('profiles')
      .select('plan, trial_ends_at, locale')
      .eq('id', session.user.id)
      .single()
      .then(({ data }) => {
        if (data?.plan) {
          setPlan(data.plan);
        }
        setTrialEndsAt(data?.trial_ends_at ?? null);
        // Sync i18next language with DB locale (highest priority)
        if (data?.locale && i18n.language !== data.locale) {
          i18n.changeLanguage(data.locale);
        }
      });
  }, [session?.user?.id]);

  if (!session) return 'VISITOR';

  const effectivePlan = getEffectiveTier(plan, trialEndsAt);

  switch (effectivePlan) {
    case 'pro':
      return 'PRO';
    case 'max':
      return 'MAX';
    case 'business':
      return 'BUSINESS';
    default:
      return 'FREE';
  }
}

/** Returns true when the current user has the given capability. */
export function useHasCapability(capability: Capability): boolean {
  const tier = useUserTier();
  return hasCapability(tier, capability);
}

/** Returns trial info for UI display (days remaining, active status). Only active for free users. */
export function useTrialStatus(): {
  isTrialActive: boolean;
  daysRemaining: number;
  hasNeverHadTrial: boolean;
} {
  const session = useAuthStore((state) => state.session);
  const [plan, setPlan] = useState<string>('free');
  const [trialEndsAt, setTrialEndsAt] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.user?.id) {
      setPlan('free');
      setTrialEndsAt(null);
      return;
    }

    supabase
      .from('profiles')
      .select('plan, trial_ends_at')
      .eq('id', session.user.id)
      .single()
      .then(({ data }) => {
        setPlan(data?.plan ?? 'free');
        setTrialEndsAt(data?.trial_ends_at ?? null);
      });
  }, [session?.user?.id]);

  if (plan !== 'free' || !trialEndsAt)
    return {
      isTrialActive: false,
      daysRemaining: 0,
      hasNeverHadTrial: plan === 'free' && trialEndsAt === null,
    };

  const end = new Date(trialEndsAt);
  const now = new Date();
  if (end <= now) return { isTrialActive: false, daysRemaining: 0, hasNeverHadTrial: false };

  const daysRemaining = Math.ceil((end.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
  return { isTrialActive: true, daysRemaining, hasNeverHadTrial: false };
}
